import { z } from "zod";
import { surveyDocumentSchema, surveyQuestionSchema } from "@event-planner/shared";

/**
 * Admin form schema for the `surveys` collection.
 *
 * Per ADR-083, the canonical Survey doc shape lives in
 * `packages/shared/src/schemas/survey.ts`. The form is structured around
 * UI tabs (Questions / Scheduling & Notification). Each question is its
 * own row in a `questions[]` field array, edited via QuestionsManager.
 *
 * Form-vs-doc orchestration:
 *  - `options` is a single string (one per line) here vs `string[]` on
 *    the doc — the service layer splits at the boundary
 *  - Presentation-metadata fields are required strings on the form;
 *    the service normalizes empty strings back to null on write
 *  - `id` on each question is generated client-side at append() time
 *    (crypto.randomUUID) and persisted onto the doc so responses can
 *    join via `answer.questionId`
 *  - Top-level fields (`scheduleMode`, `sendNotification`,
 *    `notificationTitle`, `notificationBody`) control the companion
 *    notification doc; they don't live on the Survey itself
 */
export const questionInputSchema = z.object({
  id: z.string().min(1),
  prompt: z
    .string()
    .min(1, "Question is required")
    .max(500),
  type: surveyQuestionSchema.shape.type,
  /** Newline-separated string. Service splits + trims into string[] on write. */
  options: z.string(),

  // ── Per-question presentation metadata ──
  // Required strings on the form; service normalizes "" → null on write.
  ratingAxisLabel: z.string().max(50),
  ratingScaleMin: z.string().max(50),
  ratingScaleMax: z.string().max(50),
  optionAxisLabel: z.string().max(50),
  responseLabel: z.string().max(50),
  chartSubtitle: z.string().max(100),
});

export type QuestionInputFormData = z.infer<typeof questionInputSchema>;

export const surveySchema = z
  .object({
    // ── Questions tab ──
    questions: z
      .array(questionInputSchema)
      .min(1, "At least one question is required")
      .max(20, "Surveys are capped at 20 questions"),
    sessionId: z.string(),

    // ── Scheduling & Notification tab ──
    scheduleMode: z.enum(["now", "later"]),
    scheduledAt: z.string().nullable(),
    sendNotification: z.boolean(),
    notificationTitle: z.string(),
    notificationBody: z.string(),
  })
  .superRefine((data, ctx) => {
    data.questions.forEach((q, idx) => {
      if (q.type === "single_choice") {
        const opts = q.options
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean);
        if (opts.length < 2) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "Single-choice questions need at least 2 options (one per line)",
            path: ["questions", idx, "options"],
          });
        }
      }
    });
  })
  .refine(
    (data) =>
      data.scheduleMode !== "later" ||
      (data.scheduledAt !== null && data.scheduledAt.length > 0),
    {
      message: "Pick a date and time to schedule",
      path: ["scheduledAt"],
    },
  )
  .refine(
    (data) =>
      !data.sendNotification ||
      data.notificationTitle.trim().length > 0,
    {
      message: "Notification title is required when sending a push notification",
      path: ["notificationTitle"],
    },
  )
  .refine(
    (data) =>
      !data.sendNotification ||
      data.notificationBody.trim().length > 0,
    {
      message: "Notification body is required when sending a push notification",
      path: ["notificationBody"],
    },
  );

export type SurveyFormData = z.infer<typeof surveySchema>;

/**
 * Factory for a blank question row. Used by QuestionsManager.append() and
 * SurveyCreatePage's defaultValues to seed the first question. The id is
 * a UUID so it stays stable through edits and joins to answers cleanly.
 */
export function createBlankQuestion(): QuestionInputFormData {
  return {
    id:
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `q-${Math.random().toString(36).slice(2, 10)}`,
    prompt: "",
    type: "rating",
    options: "",
    ratingAxisLabel: "",
    ratingScaleMin: "",
    ratingScaleMax: "",
    optionAxisLabel: "",
    responseLabel: "",
    chartSubtitle: "",
  };
}

// Re-export for clarity at call sites.
export { surveyDocumentSchema, surveyQuestionSchema };
